import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { Trophy } from "react-bootstrap-icons";

import dashStyles from "./AiDashboard.module.css";
import academicStyles from "../../../pages/schoolCockpit/master/academicConfiguration/Academiclist.module.css";
import AxiosInstance, { TokenService } from "../../../services/Api";
import DailyAttendanceCard from "../../../components/Chart/DailyAttendanceCard/DailyAttendanceCard";
import LineChart from "../../../components/Chart/LineChart/LineChart";
import CustomBarChart from "../../../components/Chart/CustomBarChart";
import LineAndBarChart from "../../../components/Chart/ComboChart/LineAndBarChart";
import CustomLoader from "../../../components/CustomLoader/CustomLoader";
// NOTE: verify this import path matches where CustomDropdown lives in your project.
import CustomDropdown from "../../../components/CustomDropdown/CustomDropdown";
import ClassPerformanceHeatmap, {
  MarkCell,
  SYLLABUS_MARK_BUCKETS,
} from "../../../components/Chart/Classperformanceheatmap/Classperformanceheatmap";
import SubjectEngagementInsights, {
  ScoreBadge,
} from "../../../components/Chart/Subjectengagementinsights/Subjectengagementinsights";
import { FaCalendarAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

/* ─────────────────────────────────────────────────────────────────────────
 *  Constants
 * ───────────────────────────────────────────────────────────────────────── */
const ATTENDANCE_LINE_COLOR = "#38bdf8";
const ATTENDANCE_THRESHOLD = 50;
const SEARCH_DEBOUNCE_MS = 400;

/* component state ↔ DB filter codes: 0 = No, 1 = Medium, 2 = High */
const API_RISK_MAP = { noRisk: 0, mediumRisk: 1, highRisk: 2 };

/* Stacked segments for the Remedial Performance chart (counts, not %).
 *   fail_count    → red gradient
 *   total_remedial→ GREEN gradient  (labelled "Remedial Count")
 * Segment `key` must match the field names produced by mapTrackerRow(). */
const REMEDIAL_SEGMENTS = [
  { key: "failCount", darkVar: "--red-dark", lightVar: "--red-light" },
  {
    key: "totalRemedial",
    darkVar: "--green-dark",
    lightVar: "--green-light",
  },
];

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/* 3-char month names for the attendance-trend month filter labels, e.g. "Jan 2026". */
const MONTH_NAMES_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const EMPTY_SUMMARY = {
  total: { count: 0, male: 0, female: 0 },
  present: { count: 0, male: 0, female: 0 },
  absent: { count: 0, male: 0, female: 0 },
  leave: { count: 0, male: 0, female: 0 },
};

const ACADEMIC_HEALTH_COLUMNS = [
  { key: "name", label: "Student Name", isName: true },
  { key: "class", label: "Class" },
  { key: "section", label: "Section" },
  { key: "classTeacher", label: "Class Teacher" },
  { key: "examMarks", label: "Exam Marks" },
  { key: "attendance", label: "Attendance (%)" },
  { key: "assessmentRemedial", label: "Assessment & Remedial Score (%)" },
  { key: "applicationUsage", label: "Application Usage (%)" },
  {
    key: "academic_health_score", // ← matches the mapped field
    label: "Academic Health Score",
    sortAccessor: (r) => r.academic_health_score ?? 0,
    render: (r) => <ScoreBadge value={r.academic_health_score} />,
  },
];

const TEACHER_RANKING_COLUMNS = [
  { key: "name", label: "Teacher Name", isName: true },
  { key: "syllabus", label: "Syllabus" },
  { key: "attendance", label: "Attendance" },
  { key: "assessment", label: "Assessment" },
  { key: "remedialAssessment", label: "Remedial Assessment" },
  { key: "exam", label: "Exam" },
  {
    key: "overallScore",
    label: "Overall",
    render: (r) => <ScoreBadge value={r.overallScore} />,
  },
  { key: "rank", label: "Rank" },
];

/* ─────────────────────────────────────────────────────────────────────────
 *  Pure helpers
 * ───────────────────────────────────────────────────────────────────────── */
const isAbortError = (err) =>
  err?.name === "CanceledError" ||
  err?.name === "AbortError" ||
  err?.code === "ERR_CANCELED";

/* Single abortable POST: cancels any in-flight request sharing `abortRef`,
 * then returns { data, controller }. Throws on real (non-abort) errors. */
const abortablePost = async (abortRef, endpoint, payload) => {
  abortRef.current?.abort();
  const controller = new AbortController();
  abortRef.current = controller;
  const { data } = await AxiosInstance.post(endpoint, payload, {
    signal: controller.signal,
  });
  return { data, controller };
};

const resolveSyllabusLevel = (filter) =>
  filter === "all" || filter == null ? "" : Number(filter);

const mapSummary = (summary) => ({
  total: summary?.total_students || { count: 0, male: 0, female: 0 },
  present: summary?.present || { count: 0, male: 0, female: 0 },
  absent: summary?.absent || { count: 0, male: 0, female: 0 },
  leave: summary?.leave || { count: 0, male: 0, female: 0 },
});

const mapTeacherTrendSummary = (summary) => ({
  total: summary?.total_teachers || { count: 0, male: 0, female: 0 },
  present: summary?.present || { count: 0, male: 0, female: 0 },
  absent: summary?.absent || { count: 0, male: 0, female: 0 },
  leave: summary?.leave || { count: 0, male: 0, female: 0 },
});

/* Date formatter. Always renders DD-MM-YYYY, handling every shape the APIs
 * return:
 *   "30-04-2026"                      (DD-MM-YYYY)
 *   "2026-04-24T00:00:00.000Z"        (ISO timestamp, e.g. created_at)
 *   "2026-05-01"                      (YYYY-MM-DD plain) */
const formatTrendDate = (d) => {
  if (d == null) return "";
  const s = String(d);

  // ISO timestamp → convert via Date (local), e.g. 2026-04-24T... → 24-04-2026
  if (s.includes("T")) {
    const dt = new Date(s);
    if (!Number.isNaN(dt.getTime())) {
      const day = String(dt.getDate()).padStart(2, "0");
      const month = String(dt.getMonth() + 1).padStart(2, "0");
      const year = dt.getFullYear();
      return `${day}-${month}-${year}`;
    }
  }

  const parts = s.split("-");
  if (parts.length === 3) {
    // "YYYY-MM-DD" → reorder to DD-MM-YYYY
    if (parts[0].length === 4) {
      return `${parts[2].slice(0, 2)}-${parts[1]}-${parts[0]}`;
    }
    // already "DD-MM-YYYY"
    return `${parts[0]}-${parts[1]}-${parts[2]}`;
  }
  return s;
};

const buildTrendChart = (trend, seriesLabel) => ({
  labels: (trend || []).map((e) => formatTrendDate(e.date)),
  datasets: [
    {
      label: seriesLabel,
      data: (trend || []).map((e) => e.attendance_percent),
      color: ATTENDANCE_LINE_COLOR,
    },
  ],
});

/* Build month/year filter options from the academic year's start_date,
 * capped at the earlier of (current month, end_date).
 *   value: "M-YYYY"  (e.g. "4-2026")   label: "Apr 2026" */
const buildMonthOptions = (startDate, endDate) => {
  if (!startDate) return [];
  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) return [];

  const now = new Date();
  let upper = now;
  if (endDate) {
    const end = new Date(endDate);
    if (!Number.isNaN(end.getTime()) && end < now) upper = end;
  }

  // Normalise both bounds to the first of their month for clean iteration.
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(upper.getFullYear(), upper.getMonth(), 1);

  const options = [];
  while (cursor <= last) {
    const m = cursor.getMonth(); // 0-based
    const y = cursor.getFullYear();
    options.push({
      value: `${m + 1}-${y}`,
      // 3-char month + year, no dash, e.g. "Jan 2026".
      label: `${MONTH_NAMES_SHORT[m]} ${y}`,
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return options;
};

/* "4-2026" → { month: 4, year: 2026 }; "" / "all" / unparseable → { month: "", year: "" }
 * Accepts a raw "M-YYYY" string OR an option object { value }, so it can't
 * accidentally produce NaN (which axios would serialise as null). */
const parseMonthValue = (val) => {
  const raw = val && typeof val === "object" ? val.value : val;
  if (!raw) return { month: "", year: "" };
  const [m, y] = String(raw).split("-");
  const month = Number(m);
  const year = Number(y);
  if (!Number.isFinite(month) || !Number.isFinite(year)) {
    return { month: "", year: "" };
  }
  return { month, year };
};

/* Normalise whatever CustomDropdown emits (a value string OR an option object)
 * down to the bare value string we store in state. */
const pickDropdownValue = (opt) =>
  opt && typeof opt === "object" ? (opt.value ?? "") : (opt ?? "");

/* ── Adaptive y-axis helpers ───────────────────────────────────────────────
 * The remedial counts (fail_count / total_remedial) can range from a handful
 * to thousands, and the FTR ratio sits between 0 and ~1. niceScale() rounds the
 * data's max up to a clean tick boundary (1 / 2 / 2.5 / 5 / 10 × 10ⁿ) so the
 * axis grows with the data instead of being pinned at a hardcoded max. */
const arrMax = (arr) =>
  (Array.isArray(arr) ? arr : []).reduce(
    (m, v) => (Number(v) > m ? Number(v) : m),
    0,
  );

const niceScale = (rawMax, floor = 0) => {
  // `floor` keeps a sensible axis when the data is tiny or all-zero
  // (counts → 10, ratio → 1).
  const target = Math.max(Number(rawMax) || 0, floor);
  if (!Number.isFinite(target) || target <= 0) {
    const fb = Math.max(floor, 10);
    return { max: fb, step: fb / 5 };
  }
  const rough = target / 5; // aim for ~5 ticks
  const pow = Math.pow(10, Math.floor(Math.log10(rough)));
  const frac = rough / pow;
  let niceFrac;
  if (frac <= 1) niceFrac = 1;
  else if (frac <= 2) niceFrac = 2;
  else if (frac <= 2.5) niceFrac = 2.5;
  else if (frac <= 5) niceFrac = 5;
  else niceFrac = 10;
  const step = niceFrac * pow;
  const max = Math.ceil(target / step) * step;
  return { max, step };
};

/* ── assessmentTracker row mapper ──────────────────────────────────────────
 * Normalises a tracker row at any drill level into the shape the charts read.
 * The assessment-level response is the only one whose fields are fully known;
 * for class / section / subject levels we fall back through a few likely field
 * names. If your upper-level responses use different keys, adjust the three
 * id lines + the label switch below — nothing else needs to change. */
const mapTrackerRow = (row, level) => {
  const failCount = Number(row.fail_count ?? 0);
  const totalRemedial = Number(row.total_remedial ?? 0);
  const ftrRatio = Number(row.ftr_ratio ?? 0);

  // Drill identifiers (assessment rows expose class_id/section_id/subject_id).
  const classId =
    row.class_id ?? row.student_class ?? row.student_class_type ?? "";
  const sectionId = row.section_id ?? row.student_section ?? "";
  const subjectId = row.subject_id ?? "";

  let label;
  if (level === "class") {
    // Prefer the human class name ("X") → "Class X"; fall back to class_id.
    label = `Class ${row.class_name ?? classId}`.trim();
  } else if (level === "section") {
    label =
      `Section ${row.section_id ?? row.student_section ?? sectionId}`.trim();
  } else if (level === "subject") {
    label = `${row.subject ?? row.subject_name ?? subjectId}`.trim();
  } else {
    // assessment_label can arrive as a Buffer (binary string from MySQL) -> only use it when it is text
    label =
      (typeof row.assessment_label === "string" && row.assessment_label) ||
      `Assessment ${row.assessment_no ?? ""}`.trim();
  }

  // 4th (assessment) level: the x-axis assessment name is hidden and the
  // created date is shown on hover instead. Falls back to the label when
  // created_at is absent.
  const createdAt = row.created_at ?? null;
  const dateLabel = createdAt ? formatTrendDate(createdAt) : label;

  return {
    label,
    dateLabel,
    createdAt,
    failCount,
    totalRemedial,
    ftrRatio,
    classId,
    sectionId,
    subjectId,
    raw: row,
  };
};

/* Title suffix built from the tracker's drilled-into path labels:
 *   section    → " - Class X"
 *   subject    → " - Class X - Section A"
 *   assessment → " - Class X - Section A - TAMIL" */
const buildTrackerSuffix = (tracker) => {
  const { level, path } = tracker;
  if (level === "section") return ` - ${path.classLabel}`;
  if (level === "subject")
    return ` - ${path.classLabel} - ${path.sectionLabel}`;
  if (level === "assessment")
    return ` - ${path.classLabel} - ${path.sectionLabel} - ${path.subjectLabel}`;
  return "";
};

/* AssessmentInsight bar label per drill level:
 *   class   → "Class X"          section → "Class X - A"
 *   subject → API-provided row.label */
const buildInsightLabel = (lvl, row) => {
  const type = row.student_class_type ?? row.student_class ?? "";
  if (lvl === "class") return `Class ${type}`.trim();
  if (lvl === "section")
    return `Class ${type} - ${row.student_section ?? ""}`.trim();
  return (
    row.label ||
    `${row.subject ?? ""}${row.teacher_name ? ` (${row.teacher_name})` : ""}`
  );
};

/* ── Response mappers (pure → kept at module scope) ───────────────────────── */
const mapTeacherRankingResponse = (raw) => {
  const api = raw?.data || raw || {};
  const summaryObj = api.summary || {};
  const list = api.teachers || [];
  const p = api.pagination || {};

  return {
    subjects: [
      {
        id: "teachers",
        name: "Teachers",
        summary: {
          noRisk: Number(summaryObj.no_risk ?? 0),
          mediumRisk: Number(summaryObj.medium_risk ?? 0),
          highRisk: Number(summaryObj.high_risk ?? 0),
        },
        students: list.map((teacher) => ({
          id: String(teacher.teaching_id),
          name:
            teacher.name ||
            `${teacher.first_name || ""} ${teacher.last_name || ""}`.trim(),
          // phone: teacher.mobile_no ? String(teacher.mobile_no) : "—",
          syllabus: Number(teacher.syllabus_score ?? 0),
          attendance: Number(teacher.attendance_score ?? 0),
          assessment: Number(teacher.assessment_score ?? 0),
          remedialAssessment: Number(teacher.remdial_assessment ?? 0),
          exam: Number(teacher.exam_score ?? 0),
          overallScore: Number(teacher.overall_score ?? 0),
          rank: Number(teacher.rank ?? 0),
        })),
      },
    ],
    pagination: {
      total: p.total ?? 0,
      hasPrev: p.page > 1,
      hasNext: p.page < p.total_pages,
    },
  };
};

const mapClassRankingRows = (raw, page = 1) => {
  const body = raw || {};
  const list = Array.isArray(body.data)
    ? body.data
    : Array.isArray(body)
      ? body
      : [];
  const p = body.pagination || {};

  return {
    rows: list.map((c, i) => ({
      id: `${c.class_id}-${c.section_id}`,
      name: ` ${c.class_name ?? ""} `.trim(),
      section: c.section_id ?? "—",
      class_teacher: c.class_teacher ?? "—",
      examMarks: Number(c.exam_marks ?? 0),
      attendance: Number(c.attendance_percentage ?? 0),
      assessmentRemedial: Number(c.assessment_remedial_score ?? 0),
      // applicationUsage: Number(c.application_usage ?? 0),
      unattendScore: Number(c.unattend_score ?? 0),
      overallScore: Number(c.academic_health_score ?? 0),
      rank: Number(c.rank ?? i + 1),
    })),
    pagination: {
      total: p.total_records ?? p.total ?? list.length,
      hasPrev: (p.current_page ?? page) > 1,
      hasNext: (p.current_page ?? page) < (p.total_pages ?? 1),
    },
  };
};

const mapAcademicHealthResponse = (raw, activeClassTab) => {
  const api = raw?.data || raw || {};
  const summaryObj = api.summary || {};
  const studentList = api.student || [];
  const p = api.pagination || {};
  const className = api.class_name || "—";
  return {
    subjects: [
      {
        id: activeClassTab,
        name: `Class - ${api.class_name || "Selected"}`,
        summary: {
          noRisk: Number(summaryObj["less-risk"] ?? 0),
          mediumRisk: Number(summaryObj["medium-risk"] ?? 0),
          highRisk: Number(summaryObj["high-risk"] ?? 0),
        },
        students: studentList.map((s) => ({
          id: String(s.student_id),
          name: s.name,
          phone: s.mobile_no || "—",
          section: s.section_id || "A",
          classTeacher: s.class_teacher || "—",
          class: s.class_name || "-",
          examMarks: Number(s.overall_exam_score ?? 0),
          attendance: Number(s.attendance_pct ?? 0),
          assessmentRemedial: Number(s.assessment_remedial_pct ?? 0),
          applicationUsage: Number(s.application_usage_pct ?? 0),
          overallScore: Number(s.academic_health_score ?? 0),
          academic_health_score: Number(s.academic_health_score ?? 0),
          rank: s.student_id,
        })),
      },
    ],
    pagination: {
      total: p.total ?? 0,
      hasPrev: p.has_prev ?? false,
      hasNext: p.has_next ?? false,
    },
  };
};

const mapSyllabusCompletionResponse = (raw) => {
  const api = raw?.data || raw || {};
  const classes = Array.isArray(api.classes) ? api.classes : [];

  // Use the top-level `subjects` list as the canonical column set.
  // This ensures every subject column shows even when a class has no
  // data for it (rendered as an empty MarkCell).
  const subjects = (api.subjects || []).map((s) => ({
    id: String(s.subject_id),
    name: s.subject_name,
  }));

  const rows = classes.map((c) => {
    const marks = {};
    const marksMeta = {};
    const marksInfo = {};
    (c.subjects || []).forEach((subj) => {
      const sid = String(subj.subject_id);
      // /dashboard/HM/syllabus-completion → `completion_percentage`
      marks[sid] = Number(subj.completion_percentage ?? 0);
      marksMeta[sid] = subj.staff_name || "";
      // Extra per-cell detail shown on hover over the swatch.
      marksInfo[sid] = {
        completion: Number(subj.completion_percentage ?? 0),
        toBeCovered: Number(subj.pages_to_covered ?? 0),
        actuallyCovered: Number(subj.total_completed_pages ?? 0),
      };
    });
    return {
      id: `${c.class_id}-${c.section_id}`,
      name: ` ${c.class_name ?? ""}`.trim(),
      section: c.section_id ?? "—",
      marks,
      marksMeta,
      marksInfo,
    };
  });

  const p = api.pagination || {};
  return {
    subjects,
    rows,
    pagination: {
      total: p.total ?? rows.length,
      hasPrev: p.has_prev ?? (p.current_page ?? 1) > 1,
      hasNext: p.has_next ?? (p.current_page ?? 1) < (p.total_pages ?? 1),
    },
  };
};

/* ─────────────────────────────────────────────────────────────────────────
 *  Reusable hooks
 * ───────────────────────────────────────────────────────────────────────── */

/* Trim + debounce a (string) value into a single settled value. */
const useDebouncedValue = (value, delay = SEARCH_DEBOUNCE_MS) => {
  const settle = (v) => (typeof v === "string" ? v.trim() : v);
  const [debounced, setDebounced] = useState(() => settle(value));
  useEffect(() => {
    const id = setTimeout(() => setDebounced(settle(value)), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

/* Attendance-trend loader (summary + trend list). Self-fetches on dep change. */
const useAttendanceTrend = ({
  enabled,
  endpoint,
  buildPayload,
  mapResponse,
  genericError,
}) => {
  const [state, setState] = useState({
    summary: null,
    trend: [],
    loading: true,
    error: null,
  });
  const abortRef = useRef(null);

  const load = useCallback(async () => {
    if (!enabled) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data, controller } = await abortablePost(
        abortRef,
        endpoint,
        buildPayload(),
      );
      if (controller.signal.aborted) return;

      if (data?.status && data?.data) {
        setState({ ...mapResponse(data.data), loading: false, error: null });
      } else {
        setState({
          summary: null,
          trend: [],
          loading: false,
          error: data?.message || genericError,
        });
      }
    } catch (err) {
      if (!isAbortError(err)) {
        setState({
          summary: null,
          trend: [],
          loading: false,
          error: genericError,
        });
      }
    }
  }, [enabled, endpoint, buildPayload, mapResponse, genericError]);

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  return state;
};

/* Table-section loader (status string + mapped data). `mapResponse` returns
 * undefined to flag an error state; any object → success. Self-fetches. */
const useSectionData = ({
  enabled,
  endpoint,
  buildPayload,
  mapResponse,
  isMountedRef,
}) => {
  const [data, setData] = useState();
  const [status, setStatus] = useState("success");
  const abortRef = useRef(null);

  const load = useCallback(async () => {
    if (!enabled) return;
    if (isMountedRef.current) setStatus("loading");
    try {
      const { data: raw, controller } = await abortablePost(
        abortRef,
        endpoint,
        buildPayload(),
      );
      if (controller.signal.aborted || !isMountedRef.current) return;

      const mapped = mapResponse(raw);
      if (mapped === undefined) {
        setStatus("error");
      } else {
        setData(mapped);
        setStatus("success");
      }
    } catch (err) {
      if (!isAbortError(err) && isMountedRef.current) setStatus("error");
    }
  }, [enabled, endpoint, buildPayload, mapResponse, isMountedRef]);

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  return { data, status, reload: load };
};

/* Server-side drill-down for AssessmentInsight: class → section → subject.
 * Self-loads the top-level class view on mount / credential change. */
const useAssessmentInsight = ({
  endpoint,
  school_id,
  academic_id,
  genericError,
}) => {
  const [level, setLevel] = useState("class");
  const [classId, setClassId] = useState("");
  const [selectedClassLabel, setSelectedClassLabel] = useState(""); // ← new ("X")
  const [selectedSection, setSelectedSection] = useState("");
  const [state, setState] = useState({ data: [], loading: true, error: null });
  const abortRef = useRef(null);

  const fetchInsight = useCallback(
    async ({ lvl, class_id = "", sec = "" }) => {
      if (!school_id || !academic_id) {
        setState((s) => ({ ...s, loading: false }));
        return;
      }
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const { data, controller } = await abortablePost(abortRef, endpoint, {
          school_id: Number(school_id),
          academic_year_id: Number(academic_id),
          class_id: class_id ? String(class_id) : "",
          section: sec || "",
        });
        if (controller.signal.aborted) return;

        if (data?.status && Array.isArray(data.data)) {
          setState({
            data: data.data.map((row) => ({
              label: buildInsightLabel(lvl, row),
              strong: Number(row.strong_percentage ?? 0),
              weak: Number(row.weak_percentage ?? 0),
              studentClass: row.student_class,
              studentClassType: row.student_class_type,
              studentSection: row.student_section,
              subject: row.subject,
              teacherName: row.teacher_name,
            })),
            loading: false,
            error: null,
          });
        } else {
          setState({
            data: [],
            loading: false,
            error: data?.message || genericError,
          });
        }
      } catch (err) {
        if (!isAbortError(err)) {
          setState({
            data: [],
            loading: false,
            error: err?.response?.data?.message || genericError,
          });
        }
      }
    },
    [endpoint, school_id, academic_id, genericError],
  );

  const reset = useCallback(() => {
    setLevel("class");
    setClassId("");
    setSelectedClassLabel(""); // ← reset
    setSelectedSection("");
    fetchInsight({ lvl: "class" });
  }, [fetchInsight]);

  const handleBarClick = useCallback(
    (datum, _idx, clickedLevel) => {
      if (!datum) return;
      const current = clickedLevel || level;

      if (current === "class") {
        const cid = String(datum.studentClass ?? "");
        // class display name, e.g. "X"
        const clsLabel = String(
          datum.studentClassType ?? datum.studentClass ?? "",
        );
        setClassId(cid);
        setLevel("section");
        setClassId(cid);
        setSelectedClassLabel(clsLabel);
        fetchInsight({ lvl: "section", class_id: cid });
      } else if (current === "section") {
        const cid = String(datum.studentClass ?? classId);
        const sec = String(datum.studentSection ?? "");
        setClassId(cid);
        setSelectedSection(sec);
        setLevel("subject");
        fetchInsight({ lvl: "subject", class_id: cid, sec });
      }
    },
    [level, classId, fetchInsight],
  );

  const handleBack = useCallback(() => {
    if (level === "subject") {
      setSelectedSection("");
      setLevel("section");
      fetchInsight({ lvl: "section", class_id: classId });
    } else if (level === "section") {
      setSelectedClassLabel("");
      setLevel("class");
      setClassId("");
      fetchInsight({ lvl: "class" });
    }
  }, [level, classId, fetchInsight]);

  useEffect(() => {
    reset();
    return () => abortRef.current?.abort();
  }, [reset]);

  return {
    level,
    state,
    handleBarClick,
    handleBack,
    selectedClassLabel, // ← expose
    selectedSection,
  };
};

/* ── 4-level drill-down against `dashboard/assessmentTracker` ───────────────
 * Class → Section → Subject → Assessment. Both remedial charts each get their
 * own instance so their drill state and back buttons stay independent.
 *
 * Payload sent at each level (empty ids ⇒ "give me this level's list"):
 *   class      → { school_id, academic_year, class_id:"",  section_id:"", subject_id:"" }
 *   section    → { …, class_id, section_id:"", subject_id:"" }
 *   subject    → { …, class_id, section_id, subject_id:"" }
 *   assessment → { …, class_id, section_id, subject_id }
 *
 * NOTE: `academic_year` (NOT academic_year_id) and `section_id` (NOT section),
 * matching the assessmentTracker contract. */
const useAssessmentTracker = ({
  endpoint,
  school_id,
  academic_id,
  genericError,
}) => {
  const [level, setLevel] = useState("class");
  const [path, setPath] = useState({
    classId: "",
    sectionId: "",
    subjectId: "",
    classLabel: "",
    sectionLabel: "",
    subjectLabel: "",
  });
  const [state, setState] = useState({ rows: [], loading: true, error: null });
  const abortRef = useRef(null);

  const fetchLevel = useCallback(
    async ({ lvl, class_id = "", section_id = "", subject_id = "" }) => {
      if (!school_id || !academic_id) {
        setState((s) => ({ ...s, loading: false }));
        return;
      }
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const { data, controller } = await abortablePost(abortRef, endpoint, {
          school_id: Number(school_id),
          academic_year: Number(academic_id),
          class_id: class_id === "" ? "" : class_id,
          section_id: section_id || "",
          subject_id: subject_id === "" ? "" : String(subject_id),
        });
        if (controller.signal.aborted) return;

        if (data?.status && Array.isArray(data.data)) {
          setState({
            rows: data.data.map((row) => mapTrackerRow(row, lvl)),
            loading: false,
            error: null,
          });
        } else {
          setState({
            rows: [],
            loading: false,
            error: data?.message || genericError,
          });
        }
      } catch (err) {
        if (!isAbortError(err)) {
          setState({
            rows: [],
            loading: false,
            error: err?.response?.data?.message || genericError,
          });
        }
      }
    },
    [endpoint, school_id, academic_id, genericError],
  );

  const reset = useCallback(() => {
    setLevel("class");
    setPath({
      classId: "",
      sectionId: "",
      subjectId: "",
      classLabel: "",
      sectionLabel: "",
      subjectLabel: "",
    });
    fetchLevel({ lvl: "class" });
  }, [fetchLevel]);

  /* Drill one level deeper using the clicked row's index. */
  const drillByIndex = useCallback(
    (idx) => {
      const row = state.rows[idx];
      if (!row) return;

      if (level === "class") {
        setPath((p) => ({ ...p, classId: row.classId, classLabel: row.label }));
        setLevel("section");
        fetchLevel({ lvl: "section", class_id: row.classId });
      } else if (level === "section") {
        setPath((p) => ({
          ...p,
          sectionId: row.sectionId,
          sectionLabel: row.label,
        }));
        setLevel("subject");
        fetchLevel({
          lvl: "subject",
          class_id: path.classId,
          section_id: row.sectionId,
        });
      } else if (level === "subject") {
        setPath((p) => ({
          ...p,
          subjectId: row.subjectId,
          subjectLabel: row.label,
        }));
        setLevel("assessment");
        fetchLevel({
          lvl: "assessment",
          class_id: path.classId,
          section_id: path.sectionId,
          subject_id: row.subjectId,
        });
      }
      // "assessment" is the leaf — clicking does nothing further.
    },
    [level, path.classId, path.sectionId, state.rows, fetchLevel],
  );

  /* Climb one level back up. */
  const goBack = useCallback(() => {
    if (level === "assessment") {
      setPath((p) => ({ ...p, subjectId: "", subjectLabel: "" }));
      setLevel("subject");
      fetchLevel({
        lvl: "subject",
        class_id: path.classId,
        section_id: path.sectionId,
      });
    } else if (level === "subject") {
      setPath((p) => ({ ...p, sectionId: "", sectionLabel: "" }));
      setLevel("section");
      fetchLevel({ lvl: "section", class_id: path.classId });
    } else if (level === "section") {
      setPath((p) => ({ ...p, classId: "", classLabel: "" }));
      setLevel("class");
      fetchLevel({ lvl: "class" });
    }
  }, [level, path.classId, path.sectionId, fetchLevel]);

  useEffect(() => {
    reset();
    return () => abortRef.current?.abort();
  }, [reset]);

  return { level, path, state, drillByIndex, goBack, reset };
};

/* ─────────────────────────────────────────────────────────────────────────
 *  Main HM AI Dashboard
 * ───────────────────────────────────────────────────────────────────────── */
const AiDashboard = () => {
  const { translations } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations?.labels]);

  /* ── Auth ── */
  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const academic_id = clientData?.academic_year_id || "";
  const startDate = clientData?.start_date || "";
  const endDate = clientData?.end_date || "";
  const credentialsReady = Boolean(school_id && academic_id);
  const genericError = t.somethingWrong || "Something went wrong!";
  const navigate = useNavigate();
  /* ── Labels ── */
  const studentLabel = t.studentAttendanceTrend || "Student Attendance Trend";
  const teacherLabel = t.teacherAttendanceTrend || "Teacher Attendance Trend";
  const percentageAxisLabel = t.percentage || "Percentage(%)";
  const countAxisLabel = t.count || "Count";
  const ftrAxisLabel = t.failToRemedialRatio || "Fail to Remedial Ratio";
  const medianLabel = t.median || "Median";
  const segmentLabels = useMemo(
    () => ({ strong: t.strong || "Strong", weak: t.weak || "Weak" }),
    [t.strong, t.weak],
  );
  /* Stacked-segment labels for the Remedial Performance chart (counts).
   * "Total Remedial" is now shown as "Remedial Count". */
  const remedialSegmentLabels = useMemo(
    () => ({
      failCount: t.failCount || "Fail Count",
      totalRemedial: t.remedialCount || "Remedial Count",
    }),
    [t.failCount, t.remedialCount],
  );

  /* ── Month filter options (shared option list, per-chart selection) ──
   * The "All" option carries a non-empty sentinel value ("all") so the
   * dropdown reliably shows it as the selected default. parseMonthValue()
   * still maps "all" → empty month/year, so the payload is unchanged. */
  const monthOptions = useMemo(
    () => buildMonthOptions(startDate, endDate),
    [startDate, endDate],
  );
  const monthOptionsWithAll = useMemo(
    () => [{ value: "all", label: t.all || "All" }, ...monthOptions],
    [monthOptions, t],
  );
  const [studentMonth, setStudentMonth] = useState("all"); // "all" = no filter (default)
  const [teacherMonth, setTeacherMonth] = useState("all"); // "all" = no filter (default)

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /* ── Class list → Academic Health filter tabs (auto-select first) ── */
  const [classTabs, setClassTabs] = useState([]);
  const classTabsWithAll = useMemo(
    () => [{ value: "all", label: t.all || "All" }, ...classTabs],
    [classTabs, t],
  );
  const [classListLoading, setClassListLoading] = useState(true);
  const [ahsClass, setAhsClass] = useState("");
  const SEGMENT_NOTE =
    t.subjectEngagementNote ||
    "Click on any segment of the chart to view the students included in that category. The student list will be displayed based on the selected segment.";
  useEffect(() => {
    if (!credentialsReady) {
      setClassListLoading(false);
      return;
    }
    let active = true;
    (async () => {
      try {
        const { data } = await AxiosInstance.post("/school/classList", {
          id: Number(school_id),
          academic_year: String(academic_id),
        });
        const rawClasses = data?.data || data;
        if (active && Array.isArray(rawClasses) && rawClasses.length > 0) {
          const tabs = rawClasses.map((item) => ({
            value: String(item.class_id),
            label: item.domain_id || "Selected",
          }));
          const lastTab = tabs[tabs.length - 1];
          setClassTabs(tabs);
          setAhsClass(lastTab.value);
          setSyllabusFilter(lastTab.value);
        }
      } catch (err) {
        console.error("Error fetching class listing:", err);
      } finally {
        if (active) setClassListLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [credentialsReady, school_id, academic_id]);

  const showInitialLoader = classListLoading && !ahsClass;

  /* ── Attendance trends ── */
  /* HM student trend → payload: { school_id, academic_id, month, year } */
  const buildStudentTrendPayload = useCallback(() => {
    const { month, year } = parseMonthValue(studentMonth);
    return {
      school_id: Number(school_id),
      academic_id: Number(academic_id),
      month,
      year,
    };
  }, [school_id, academic_id, studentMonth]);
  const mapStudentTrend = useCallback(
    (d) => ({
      summary: mapSummary(d.summary),
      trend: (Array.isArray(d.trend) ? d.trend : []).map((e) => {
        const dt = e.details || {};
        return {
          date: e.date,
          attendance_percent: e.attendance_percent ?? 0,
          present_count: dt.present?.count ?? 0,
          absent_count: dt.absent?.count ?? 0,
          leave_count: dt.leave?.count ?? 0,
          total_count: dt.total_students?.count ?? 0,
        };
      }),
    }),
    [],
  );
  const student = useAttendanceTrend({
    enabled: Boolean(school_id),
    endpoint: "dashboard/HM/AttendanceTrend",
    buildPayload: buildStudentTrendPayload,
    mapResponse: mapStudentTrend,
    genericError,
  });

  /* Teacher trend → payload: { school_id, academic_year, month, year } */
  const buildTeacherTrendPayload = useCallback(() => {
    const { month, year } = parseMonthValue(teacherMonth);
    return {
      school_id: Number(school_id),
      academic_year: Number(academic_id),
      month,
      year,
    };
  }, [school_id, academic_id, teacherMonth]);
  const mapTeacherTrend = useCallback(
    (d) => ({
      summary: mapTeacherTrendSummary(d.summary),
      trend: (d.attendance_trend || []).map((e) => {
        const dt = e.details || {};
        return {
          date: e.date,
          attendance_percent: e.attendance_percentage ?? 0,
          present_count: dt.present?.count ?? 0,
          absent_count: dt.absent?.count ?? 0,
          leave_count: dt.leave?.count ?? 0,
          total_count: dt.total_teachers?.count ?? 0,
        };
      }),
    }),
    [],
  );
  /* Hover tooltip → date (title) + Present Percentage, Present count,
   * Absent count, Leave count. */
  const studentTooltipFormatter = useCallback(
    (idx) => {
      const row = student.trend[idx];
      if (!row) return null;
      return [
        `${t.presentPercentage || "Present Percentage"}: ${row.attendance_percent}%`,
        `${t.presentCount || "Present Count"}: ${row.present_count}`,
        `${t.absentCount || "Absent Count"}: ${row.absent_count}`,
        `${t.leaveCount || "Leave Count"}: ${row.leave_count}`,
      ];
    },
    [student.trend, t],
  );

  const teacher = useAttendanceTrend({
    enabled: Boolean(school_id),
    endpoint: "dashboard/teacher-attendance-trend",
    buildPayload: buildTeacherTrendPayload,
    mapResponse: mapTeacherTrend,
    genericError,
  });
  /* Hover tooltip → date (title) + Present Percentage, Present count,
   * Absent count, Leave count. */
  const teacherTooltipFormatter = useCallback(
    (idx) => {
      const row = teacher.trend[idx];
      if (!row) return null;
      return [
        `${t.presentPercentage || "Present Percentage"}: ${row.attendance_percent}%`,
        `${t.presentCount || "Present Count"}: ${row.present_count}`,
        `${t.absentCount || "Absent Count"}: ${row.absent_count}`,
        `${t.leaveCount || "Leave Count"}: ${row.leave_count}`,
      ];
    },
    [teacher.trend, t],
  );
  const studentChart = useMemo(
    () => buildTrendChart(student.trend, studentLabel),
    [student.trend, studentLabel],
  );
  const teacherChart = useMemo(
    () => buildTrendChart(teacher.trend, teacherLabel),
    [teacher.trend, teacherLabel],
  );
  const emptyTrendMessage = useCallback(
    (s) =>
      s.loading
        ? t.loading || "Loading..."
        : s.error ||
          t.noAttendanceDataAvailable ||
          "No attendance data available",
    [t],
  );

  /* ── Assessment drill-down chart (strong/weak — unchanged) ── */
  const assessment = useAssessmentInsight({
    endpoint: "dashboard/HM/AssessmentInsight",
    school_id,
    academic_id,
    genericError,
  });

  /* ── Remedial trackers — both hit dashboard/assessmentTracker ──────────────
   * Two independent instances so each chart owns its own 4-level drill state.
   *   remPerf → Remedial Assessment Performance Insights (fail + remedial)
   *   remFtr  → Remedial Assessment Fail Count Ratio (ftr_ratio) */
  const remPerf = useAssessmentTracker({
    endpoint: "dashboard/assessmentTracker",
    school_id,
    academic_id,
    genericError,
  });
  const remFtr = useAssessmentTracker({
    endpoint: "dashboard/assessmentTracker",
    school_id,
    academic_id,
    genericError,
  });

  /* Performance: left axis is the stacked total (fail + remedial). Used by both
   * the stacked CustomBarChart (class/section/subject) and the L4 bars. */
  const remPerfScale = useMemo(
    () =>
      niceScale(
        arrMax(remPerf.state.rows.map((r) => r.failCount + r.totalRemedial)),
        10,
      ),
    [remPerf.state.rows],
  );

  /* Performance assessment-level chart: stacked bars only (Fail Count +
   * Remedial Count). FTR line / right axis are removed and the x-axis names
   * are hidden; the assessment label is the tooltip title and the hover body
   * shows Date + Fail Count + Remedial Count + FTR. */
  const remPerfComboData = useMemo(() => {
    const rows = remPerf.state.rows;
    return {
      // Title = "Assessment 1"; x-axis ticks stay hidden via showXTicks={false}.
      labels: rows.map((r) => r.label),
      datasets: [
        {
          label: t.failCount || "Fail Count",
          type: "bar",
          data: rows.map((r) => r.failCount),
          gradientDarkVar: "--red-dark",
          gradientLightVar: "--red-light",
          colorVar: "--red-dark",
          stack: "bars",
          yAxisID: "y",
        },
        {
          label: t.remedialCount || "Remedial Count",
          type: "bar",
          data: rows.map((r) => r.totalRemedial),
          gradientDarkVar: "--green-dark",
          gradientLightVar: "--green-light",
          colorVar: "--green-dark",
          stack: "bars",
          yAxisID: "y",
        },
      ],
    };
  }, [remPerf.state.rows, t]);

  /* Title lines (no colour swatch): "Assessment 11" + "12-05-2026". */
  const remPerfComboTitle = useCallback(
    (dataIndex) => {
      const row = remPerf.state.rows[dataIndex];
      if (!row) return "";
      return [row.label, row.dateLabel];
    },
    [remPerf.state.rows],
  );

  /* Assessment-level (combo) hover body → Fail Count → Remedial Count → FTR.
   * Fail sits on the red dataset (0); Remedial + FTR on the green dataset (1). */
  const remPerfComboTooltip = useCallback(
    (dataIndex, datasetIndex) => {
      const row = remPerf.state.rows[dataIndex];
      if (!row) return null;
      if (datasetIndex === 0)
        return `${t.failCount || "Fail Count"}: ${row.failCount}`;
      return [
        `${t.remedialCount || "Remedial Count"}: ${row.totalRemedial}`,
        `${t.ftr || "FTR"}: ${row.ftrRatio.toFixed(2)}`,
      ];
    },
    [remPerf.state.rows, t],
  );

  /* class / section / subject levels (stacked CustomBarChart) → append the FTR
   * line after the Fail Count + Remedial Count segments, so every drill level
   * reads Fail Count → Remedial Count → FTR. */
  const remPerfAfterBody = useCallback(
    (dataIndex) => {
      const row = remPerf.state.rows[dataIndex];
      if (!row) return null;
      return `${t.ftr || "FTR"}: ${row.ftrRatio.toFixed(2)}`;
    },
    [remPerf.state.rows, t],
  );

  const remFtrData = useMemo(() => {
    const rows = remFtr.state.rows;
    return {
      labels: rows.map((r) => r.label),
      datasets: [
        {
          label: t.ftr || "FTR",
          type: "bar",
          data: rows.map((r) => r.ftrRatio),
          gradientDarkVar: "--red-dark",
          gradientLightVar: "--red-light",
          colorVar: "--red-dark",
        },
        {
          label: t.ftrTrend || "FTR Trend",
          type: "line",
          data: rows.map((r) => r.ftrRatio),
          colorVar: "--violet-dark",
          tension: 0.45,
          pointRadius: 4,
        },
      ],
    };
  }, [remFtr.state.rows, t]);

  const remFtrScale = useMemo(
    () => niceScale(arrMax(remFtr.state.rows.map((r) => r.ftrRatio)), 1),
    [remFtr.state.rows],
  );

  const remFtrTitle = useCallback(
    (dataIndex) => {
      const row = remFtr.state.rows[dataIndex];
      if (!row) return "";
      if (remFtr.level === "assessment") return [row.label, row.dateLabel];
      return row.label;
    },
    [remFtr.state.rows, remFtr.level],
  );

  /* FTR hover body → Fail Count → Remedial Count → FTR at EVERY drill level.
   * Everything sits on the bar dataset (0); the trend line row is suppressed. */
  const remFtrTooltip = useCallback(
    (dataIndex, datasetIndex) => {
      const row = remFtr.state.rows[dataIndex];
      if (!row) return null;
      if (datasetIndex !== 0) return [];
      return [
        `${t.ftr || "FTR"}: ${row.ftrRatio.toFixed(2)}`,
        `${t.failCount || "Fail Count"}: ${row.failCount}`,
        `${t.remedialCount || "Remedial Count"}: ${row.totalRemedial}`,
      ];
    },
    [remFtr.state.rows, t],
  );

  /* ── Syllabus Completion (heatmap) ── */
  const [syllabusFilter, setSyllabusFilter] = useState("all");
  const [crFilter, setCrFilter] = useState("all"); // default All
  const [heatmapPage, setHeatmapPage] = useState(1);
  const [heatmapLimit, setHeatmapLimit] = useState(10);
  const [heatmapSearch, setHeatmapSearch] = useState("");
  const debouncedHeatmapSearch = useDebouncedValue(heatmapSearch);

  const buildHeatmapPayload = useCallback(
    () => ({
      school_id: Number(school_id),
      academic_id: Number(academic_id),
      class_id: syllabusFilter === "all" ? "" : Number(syllabusFilter),
      section_id: "",
      page: heatmapPage, // ← was hardcoded 1
      limit: heatmapLimit, // HM = all subjects across all classes
    }),
    [
      school_id,
      academic_id,
      heatmapPage, // ← add
      heatmapLimit,
      syllabusFilter,
    ],
  );

  const heatmap = useSectionData({
    enabled: credentialsReady && !classListLoading,
    endpoint: "/dashboard/HM/syllabus-completion", // ← was /dashboard/HM/syllabus-completion
    buildPayload: buildHeatmapPayload,
    mapResponse: mapSyllabusCompletionResponse,
    isMountedRef,
  });

  useEffect(() => {
    setHeatmapPage(1);
  }, [syllabusFilter, heatmapLimit, debouncedHeatmapSearch]);

  const syllabusColumns = useMemo(() => {
    const subjectCols = (heatmap.data?.subjects || []).map((subj) => ({
      key: `subj_${subj.id}`,
      label: subj.name,
      sortAccessor: (r) => r.marks?.[subj.id] ?? 0,
      render: (r) => {
        const info = r.marksInfo?.[subj.id];
        return (
          <MarkCell
            mark={r.marks?.[subj.id]}
            subLabel={r.marksMeta?.[subj.id]}
            buckets={SYLLABUS_MARK_BUCKETS}
            tooltipLines={
              info
                ? [
                    `${t.completedPercentage || "Completed Percentage"}: ${info.completion}%`,
                    `${t.toBeCovered || "To be Covered"}: ${info.toBeCovered}`,
                    `${t.actuallyCovered || "Actually covered"}: ${info.actuallyCovered}`,
                  ]
                : null
            }
          />
        );
      },
    }));
    return [
      {
        key: "name",
        label: t.classes || "Classes",
        isName: true,
        sortAccessor: (r) => (r.name || "").toLowerCase(),
      },
      {
        key: "section",
        label: t.section || "Section",
        sortAccessor: (r) => String(r.section ?? "").toLowerCase(),
      },
      ...subjectCols,
    ];
  }, [heatmap.data, t]);

  /* ── Academic Health Score ── */
  const [ahsRisk, setAhsRisk] = useState("");
  const [ahsSearch, setAhsSearch] = useState("");
  const [ahsPage, setAhsPage] = useState(1);
  const [ahsLimit, setAhsLimit] = useState(10);
  const debouncedAhsSearch = useDebouncedValue(ahsSearch);

  const buildAhsPayload = useCallback(
    () => ({
      school_id: Number(school_id),
      academic_id: Number(academic_id),
      class_id: Number(ahsClass) || "",
      risk_filter: API_RISK_MAP[ahsRisk] ?? 2,
      page: ahsPage,
      limit: ahsLimit,
      search: debouncedAhsSearch,
    }),
    [
      school_id,
      academic_id,
      ahsClass,
      ahsRisk,
      ahsPage,
      ahsLimit,
      debouncedAhsSearch,
    ],
  );
  const mapAhs = useCallback(
    (raw) =>
      raw?.status && raw?.data
        ? mapAcademicHealthResponse(raw.data, ahsClass)
        : undefined,
    [ahsClass],
  );
  const ahs = useSectionData({
    enabled: credentialsReady && Boolean(ahsClass),
    endpoint: "/dashboard/HM/academic-health-score",
    buildPayload: buildAhsPayload,
    mapResponse: mapAhs,
    isMountedRef,
  });

  useEffect(() => {
    setAhsPage(1);
  }, [ahsClass, ahsRisk, ahsLimit, debouncedAhsSearch]);

  /* ── Teacher Ranking ── */
  const [trRisk, setTrRisk] = useState("highRisk");
  const [trSearch, setTrSearch] = useState("");
  const [trPage, setTrPage] = useState(1);
  const [trLimit, setTrLimit] = useState(10);
  const debouncedTrSearch = useDebouncedValue(trSearch);

  const buildTrPayload = useCallback(
    () => ({
      school_id: Number(school_id),
      academic_year: Number(academic_id),
      risk_filter: API_RISK_MAP[trRisk] ?? 2,
      page: trPage,
      limit: trLimit,
      search: debouncedTrSearch,
    }),
    [school_id, academic_id, trRisk, trPage, trLimit, debouncedTrSearch],
  );
  const mapTr = useCallback(
    (raw) =>
      raw?.status && raw?.data
        ? mapTeacherRankingResponse(raw.data)
        : undefined,
    [],
  );
  const tr = useSectionData({
    enabled: credentialsReady,
    endpoint: "/dashboard/hm/teacher-ranking",
    buildPayload: buildTrPayload,
    mapResponse: mapTr,
    isMountedRef,
  });

  useEffect(() => {
    setTrPage(1);
  }, [trRisk, trLimit, debouncedTrSearch]);

  /* ── Class Ranking ── */
  const [crSearch, setCrSearch] = useState("");
  const [crPage, setCrPage] = useState(1);
  const [crLimit, setCrLimit] = useState(10);
  const debouncedCrSearch = useDebouncedValue(crSearch);

  const buildCrPayload = useCallback(
    () => ({
      school_id: Number(school_id),
      academic_year: Number(academic_id),
      class_id: crFilter === "all" ? "" : Number(crFilter), // ← if API supports it
      page: crPage,
      limit: crLimit,
      search: debouncedCrSearch,
    }),
    [school_id, academic_id, crFilter, crPage, crLimit, debouncedCrSearch],
  );
  const mapCr = useCallback(
    (raw) => (raw?.status ? mapClassRankingRows(raw, crPage) : undefined),
    [crPage],
  );
  const cr = useSectionData({
    enabled: credentialsReady,
    endpoint: "/dashboard/overall-classwise-academic-health-score",
    buildPayload: buildCrPayload,
    mapResponse: mapCr,
    isMountedRef,
  });

  useEffect(() => {
    setCrPage(1);
  }, [crFilter, crLimit, debouncedCrSearch]);

  const classRankingColumns = useMemo(
    () => [
      {
        key: "name",
        label: t.class || "Class",
        isName: true,
        sortAccessor: (r) => (r.name || "").toLowerCase(),
      },
      {
        key: "section",
        label: t.section || "Section",
        sortAccessor: (r) => String(r.section ?? "").toLowerCase(),
      },
      {
        key: "class_teacher",
        label: t.classTeacher || "Class Teacher",
        sortAccessor: (r) => String(r.section ?? "").toLowerCase(),
      },
      { key: "examMarks", label: t.examMarks || "Exam Marks" },
      { key: "attendance", label: t.attendancePct || "Attendance (%)" },
      {
        key: "assessmentRemedial",
        label: t.assessmentRemedialScore || "Assessment & Remedial Score (%)",
      },
      {
        key: "unattendScore",
        label: t.unattendCount || "Unattend Count",
      },
      // {
      //   key: "applicationUsage",
      //   label: t.applicationUsage || "Application Usage",
      // },
      {
        key: "overallScore",
        label: t.classAcademicHealthScore || "Class Academic Health Score",
        render: (r) => <ScoreBadge value={r.overallScore} />,
      },
      {
        key: "rank",
        label: t.rank || "Rank",
        sortAccessor: (r) => (r.rank > 0 ? r.rank : Number.MAX_SAFE_INTEGER),
        render: (r) => (r.rank > 0 ? r.rank : "—"),
      },
    ],
    [t],
  );

  /* ── Initial loader gate ── */
  if (showInitialLoader) {
    return (
      <div
        className={dashStyles.card}
        style={{ minHeight: 300, position: "relative" }}
      >
        <CustomLoader label={t.loading} />
      </div>
    );
  }
  const buildInsightSuffixes = (insight) => ({
    sectionWiseSuffix: insight.selectedClassLabel
      ? ` - ${insight.selectedClassLabel}`
      : t.sectionWiseTitleSuffix || " - Section Wise",
    subjectWiseSuffix: insight.selectedClassLabel
      ? ` - ${insight.selectedClassLabel} ${insight.selectedSection}`.trimEnd()
      : t.subjectWiseTitleSuffix || " - Subject Wise",
  });
  const handleClick = (studentId) => {
    navigate("/user/studentCreate", {
      state: { ID: studentId },
    });
  };
  const assessmentSuffixes = buildInsightSuffixes(assessment);

  /* Remedial Performance title suffixes come from the tracker's drilled path. */
  const remPerfSuffixes = {
    sectionWiseSuffix: remPerf.path.classLabel
      ? ` - ${remPerf.path.classLabel}`
      : t.sectionWiseTitleSuffix || " - Section Wise",
    subjectWiseSuffix: remPerf.path.classLabel
      ? ` - ${remPerf.path.classLabel} - ${remPerf.path.sectionLabel}`.trimEnd()
      : t.subjectWiseTitleSuffix || " - Subject Wise",
  };

  /* Month dropdowns — only used for the two attendance-trend charts. */
  const studentMonthDropdown = (
    <CustomDropdown
      name="studentMonth"
      value={studentMonth}
      onChange={(opt) => setStudentMonth(pickDropdownValue(opt))}
      options={monthOptionsWithAll}
      placeholder={t.selectMonth || "Select Month"}
      isSearchable={false}
    />
  );
  const teacherMonthDropdown = (
    <CustomDropdown
      name="teacherMonth"
      value={teacherMonth}
      onChange={(opt) => setTeacherMonth(pickDropdownValue(opt))}
      options={monthOptionsWithAll}
      placeholder={t.selectMonth || "Select Month"}
      isSearchable={false}
    />
  );

  return (
    <div className={academicStyles.container} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div
        className={dashStyles.headerLine}
        style={{ justifyContent: "space-between" }}
      >
        <div className={dashStyles.leftSection}>
          <h1 className={dashStyles.headerTitle}> HM Dashboard </h1>
        </div>
      </div>
      {/* Student Attendance Trend */}
      <div className={dashStyles.row}>
        <div className={`${dashStyles.col} ${dashStyles.col12}`}>
          <div className={dashStyles.card}>
            <LineChart
              icon={<FaCalendarAlt />}
              title={studentLabel}
              showInfoIcon={false}
              showFilters={false}
              selectControl={studentMonthDropdown}
              labels={studentChart.labels}
              datasets={studentChart.datasets}
              height={280}
              yMin={0}
              yMax={100}
              yStepSize={10}
              yTickSuffix="%"
              yAxisLabel={percentageAxisLabel}
              threshold={ATTENDANCE_THRESHOLD}
              thresholdLabel={medianLabel}
              showThresholdLegend
              emptyMessage={emptyTrendMessage(student)}
              tooltipFormatter={studentTooltipFormatter}
            />
          </div>
        </div>
      </div>

      {/* Teacher Attendance Trend */}
      <div className={dashStyles.row}>
        <div className={`${dashStyles.col} ${dashStyles.col12}`}>
          <div className={dashStyles.card}>
            <LineChart
              icon={<FaCalendarAlt />}
              title={teacherLabel}
              showInfoIcon={false}
              showFilters={false}
              selectControl={teacherMonthDropdown}
              labels={teacherChart.labels}
              datasets={teacherChart.datasets}
              height={280}
              yMin={0}
              yMax={100}
              yStepSize={10}
              yTickSuffix="%"
              yAxisLabel={percentageAxisLabel}
              threshold={ATTENDANCE_THRESHOLD}
              thresholdLabel={medianLabel}
              showThresholdLegend
              emptyMessage={emptyTrendMessage(teacher)}
              tooltipFormatter={teacherTooltipFormatter}
            />
          </div>
        </div>
      </div>

      {/* Syllabus Completion */}
      <div className={dashStyles.row}>
        <div className={`${dashStyles.col} ${dashStyles.col12}`}>
          <div className={dashStyles.card}>
            <ClassPerformanceHeatmap
              title={t.syllabusCompletion || "Syllabus Completion"}
              columns={syllabusColumns}
              data={heatmap.data}
              status={heatmap.status}
              page={heatmapPage}
              perPage={heatmapLimit}
              legendBuckets={SYLLABUS_MARK_BUCKETS}
              search={heatmapSearch}
              searchEnable={false}
              onPageChange={setHeatmapPage}
              onLimitChange={setHeatmapLimit}
              onSearchChange={setHeatmapSearch}
              onRetry={heatmap.reload}
              showPeriod={false}
              showSummary={false}
              showLegend
              showFilter
              filterValue={syllabusFilter}
              onFilterChange={setSyllabusFilter}
              filterOptions={classTabsWithAll}
            />
          </div>
        </div>
      </div>

      {/* Assessment Performance Insights */}
      <div className={dashStyles.row}>
        <div className={`${dashStyles.col} ${dashStyles.col12}`}>
          <div className={dashStyles.card}>
            <CustomBarChart
              stacked
              serverDrilldown
              bottomHint
              level={assessment.level}
              icon={<i className="fa-solid fa-chart-column" />}
              title={
                t.assessmentPerformanceInsights ||
                "Assessment Performance Insights"
              }
              info={
                t.assessmentInsightsInfo ||
                "Click a class to drill into sections, then subjects."
              }
              data={assessment.state.data}
              loading={assessment.state.loading}
              error={assessment.state.error}
              segmentLabels={segmentLabels}
              {...assessmentSuffixes}
              /* Preserve the original 0–100 % look for this card. */
              maxY={100}
              stepSize={20}
              /* y-axis title "Percentage(%)" + "%" suffix on ticks (valueSuffix
               * defaults to "%"). */
              yAxisLabel={percentageAxisLabel}
              /* class-level hint removed per request */
              classHint=""
              sectionHint={
                t.clickSectionForSubjectBreakdown ||
                "Click any section to view subject-wise breakdown"
              }
              onBarClick={assessment.handleBarClick}
              onBack={assessment.handleBack}
            />
          </div>
        </div>
      </div>

      {/* Remedial Assessment Performance Insights
       *   class / section / subject → stacked CustomBarChart (Fail + Remedial)
       *   assessment                → stacked bars only (Fail + Remedial Count);
       *                               NO FTR line, NO right axis, NO x labels,
       *                               created date shown on hover. */}
      <div className={dashStyles.row}>
        <div className={`${dashStyles.col} ${dashStyles.col12}`}>
          <div className={dashStyles.card}>
            {remPerf.level === "assessment" ? (
              <LineAndBarChart
                icon={<i className="fa-solid fa-chart-column" />}
                title={
                  (t.remedialAssessmentPerformanceInsights ||
                    "Remedial Assessment Performance Insights") +
                  buildTrackerSuffix(remPerf)
                }
                labels={remPerfComboData.labels}
                datasets={remPerfComboData.datasets}
                loading={remPerf.state.loading}
                error={remPerf.state.error}
                height={340}
                stackedBars
                /* x-axis assessment names hidden; date shows on hover */
                showXTicks={false}
                yMin={0}
                yMax={remPerfScale.max}
                yStepSize={remPerfScale.step}
                yAxisLabel={countAxisLabel}
                canGoBack
                onBack={remPerf.goBack}
                emptyMessage={t.noDataAvailable || "No data available"}
                note={
                  t.remedialComboNote ||
                  "Assessment-wise: stacked bars show Fail Count + Remedial Count. Hover a bar for its date."
                }
                tooltipTitleFormatter={remPerfComboTitle}
                tooltipFormatter={remPerfComboTooltip}
              />
            ) : (
              <CustomBarChart
                stacked
                serverDrilldown
                bottomHint
                serverDrillLevels={["class", "section", "subject"]}
                level={remPerf.level}
                icon={<i className="fa-solid fa-chart-column" />}
                title={
                  t.remedialAssessmentPerformanceInsights ||
                  "Remedial Assessment Performance Insights"
                }
                info={
                  t.remedialInsightsInfo ||
                  "Click a class → section → subject, then open the assessment-wise trend."
                }
                data={remPerf.state.rows}
                loading={remPerf.state.loading}
                error={remPerf.state.error}
                valueSuffix=""
                maxY={remPerfScale.max}
                stepSize={remPerfScale.step}
                /* y-axis title "Count" (numbers stay raw via valueSuffix=""). */
                yAxisLabel={countAxisLabel}
                /* Hover → Fail Count → Remedial Count (segments) → FTR (extra). */
                tooltipAfterBody={remPerfAfterBody}
                segments={REMEDIAL_SEGMENTS}
                segmentLabels={remedialSegmentLabels}
                sectionWiseSuffix={remPerfSuffixes.sectionWiseSuffix}
                subjectWiseSuffix={remPerfSuffixes.subjectWiseSuffix}
                /* All bottom hints removed per request (section/back text). */
                classHint=""
                sectionHint=""
                backHint=""
                subjectHint={
                  t.clickSubjectForAssessmentBreakdown ||
                  "Click any subject to view assessment-wise trend"
                }
                onBarClick={(_row, idx) => remPerf.drillByIndex(idx)}
                onBack={remPerf.goBack}
              />
            )}
          </div>
        </div>
      </div>

      {/* Remedial Assessment Fail Count Ratio (FTR) — live, 4-level drilldown */}
      <div className={dashStyles.row}>
        <div className={`${dashStyles.col} ${dashStyles.col12}`}>
          <div className={dashStyles.card}>
            <LineAndBarChart
              icon={<i className="fa-solid fa-chart-column" />}
              title={
                (t.remedialFtrInsights ||
                  "Remedial Assessment Fail Count Ratio (FTR)") +
                buildTrackerSuffix(remFtr)
              }
              labels={remFtrData.labels}
              datasets={remFtrData.datasets}
              loading={remFtr.state.loading}
              error={remFtr.state.error}
              height={320}
              yMin={0}
              yMax={remFtrScale.max}
              yStepSize={remFtrScale.step}
              yAxisLabel={ftrAxisLabel}
              /* assessment level: hide x-axis names, show date on hover */
              showXTicks={remFtr.level !== "assessment"}
              barDrilldown={remFtr.level !== "assessment"}
              canGoBack={remFtr.level !== "class"}
              onBack={remFtr.goBack}
              onBarClick={(idx) => remFtr.drillByIndex(idx)}
              emptyMessage={t.noDataAvailable || "No data available"}
              note={
                t.ftrNote ||
                "Click a bar to drill down: Class → Section → Subject → Assessment."
              }
              tooltipTitleFormatter={remFtrTitle}
              tooltipFormatter={remFtrTooltip}
            />
          </div>
        </div>
      </div>
      {/* Teacher Ranking */}
      <div className={dashStyles.row}>
        <div className={`${dashStyles.col} ${dashStyles.col12}`}>
          <div className={dashStyles.card}>
            <SubjectEngagementInsights
              title={t.teacherRanking || "Teacher Ranking"}
              icon={<Trophy />}
              layout="split"
              searchPlaceholder="Search Teacher Name"
              data={tr.data}
              status={tr.status}
              columns={TEACHER_RANKING_COLUMNS}
              selectedSubjectId="teachers"
              selectedRiskKey={trRisk}
              onSubjectSegmentClick={(_subjectId, riskKey) =>
                setTrRisk(riskKey)
              }
              showPageSize
              perPage={trLimit}
              onLimitChange={setTrLimit}
              showSearch
              search={trSearch}
              onSearchChange={setTrSearch}
              showPagination
              page={trPage}
              total={tr.data?.pagination?.total ?? 0}
              hasPrev={tr.data?.pagination?.hasPrev}
              hasNext={tr.data?.pagination?.hasNext}
              onPageChange={setTrPage}
              note={SEGMENT_NOTE}
              notePosition="header"
              onRetry={tr.reload}
            />
          </div>
        </div>
      </div>
      {/* Class Ranking */}
      <div className={dashStyles.row}>
        <div className={`${dashStyles.col} ${dashStyles.col12}`}>
          <div className={dashStyles.card}>
            <ClassPerformanceHeatmap
              title={t.classRanking || "Class Ranking"}
              columns={classRankingColumns}
              searchPlaceholder="Search Teacher Name"
              data={cr.data}
              status={cr.status}
              page={crPage}
              perPage={crLimit}
              search={crSearch}
              onPageChange={setCrPage}
              onLimitChange={setCrLimit}
              onSearchChange={setCrSearch}
              onRetry={cr.reload}
              showPeriod={false}
              showSummary={false}
              filterValue={crFilter}
              showLegend
              showFilter
              onFilterChange={setCrFilter}
              filterOptions={classTabsWithAll}
            />
          </div>
        </div>
      </div>

      {/* Academic Health Score Insights */}
      <div className={dashStyles.row}>
        <div className={`${dashStyles.col} ${dashStyles.col12}`}>
          <div className={dashStyles.card}>
            <SubjectEngagementInsights
              title={
                t.academicHealthScoreInsights ||
                "Academic Health Score Insights"
              }
              layout="split"
              data={ahs.data}
              status={ahs.status}
              columns={ACADEMIC_HEALTH_COLUMNS}
              selectedSubjectId={ahsClass}
              selectedRiskKey={ahsRisk}
              onSubjectSegmentClick={(_subjectId, riskKey) =>
                setAhsRisk(riskKey)
              }
              handleClick={handleClick}
              showFilter={classTabs.length > 0}
              filterOptions={classTabsWithAll}
              filterValue={ahsClass}
              onFilterChange={setAhsClass}
              showPageSize
              perPage={ahsLimit}
              onLimitChange={setAhsLimit}
              showSearch
              search={ahsSearch}
              onSearchChange={setAhsSearch}
              showPagination
              page={ahsPage}
              total={ahs.data?.pagination?.total ?? 0}
              hasPrev={ahs.data?.pagination?.hasPrev}
              hasNext={ahs.data?.pagination?.hasNext}
              onPageChange={setAhsPage}
              note={SEGMENT_NOTE}
              notePosition="header"
              onRetry={ahs.reload}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiDashboard;
